/**
 * Cron expression explainer — pure, deterministic. Safe to render server-side.
 *
 * Parses a standard 5-field cron expression (minute hour day-of-month month
 * day-of-week) supporting `*`, lists `a,b`, ranges `a-b`, steps `* / n` and
 * `a-b/n`, plus named months (jan…dec) and weekdays (sun…sat). Produces a
 * per-field breakdown and a combined plain-English sentence, or an error.
 */

export type CronFieldKey = "minute" | "hour" | "dom" | "month" | "dow";

export type ExplainResult =
  | {
      ok: true;
      text: string;
      fields: Record<CronFieldKey, string>;
    }
  | { ok: false; error: string };

type Kind =
  | { t: "all" }
  | { t: "step"; n: number }
  | { t: "single"; v: number }
  | { t: "range"; a: number; b: number }
  | { t: "rangeStep"; a: number; b: number; n: number }
  | { t: "list"; items: Kind[] };

interface FieldMeta {
  key: CronFieldKey;
  min: number;
  max: number;
  /** Phrase for an all-`*` field in the per-field breakdown. */
  allLabel: string;
  unitPlural: string;
  names?: Record<string, number>;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DOW_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const MONTH_IDX: Record<string, number> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};
const DOW_IDX: Record<string, number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

const META: Record<CronFieldKey, FieldMeta> = {
  minute: {
    key: "minute",
    min: 0,
    max: 59,
    allLabel: "Every minute",
    unitPlural: "minutes",
  },
  hour: {
    key: "hour",
    min: 0,
    max: 23,
    allLabel: "Every hour",
    unitPlural: "hours",
  },
  dom: {
    key: "dom",
    min: 1,
    max: 31,
    allLabel: "Every day of the month",
    unitPlural: "days",
  },
  month: {
    key: "month",
    min: 1,
    max: 12,
    allLabel: "Every month",
    unitPlural: "months",
    names: MONTH_IDX,
  },
  dow: {
    key: "dow",
    min: 0,
    max: 7,
    allLabel: "Every day of the week",
    unitPlural: "days of the week",
    names: DOW_IDX,
  },
};

function resolveValue(token: string, meta: FieldMeta): number | null {
  const t = token.toLowerCase();
  if (meta.names && Object.hasOwn(meta.names, t)) return meta.names[t];
  if (/^\d+$/.test(token)) return Number(token);
  return null;
}

type AtomOrError = Kind | { error: string };

function parseAtom(atom: string, meta: FieldMeta): AtomOrError {
  if (atom.includes("/")) {
    const [base, stepStr] = atom.split("/");
    if (!/^\d+$/.test(stepStr) || Number(stepStr) < 1) {
      return { error: `Invalid step "${atom}" in ${meta.key}.` };
    }
    const n = Number(stepStr);
    if (base === "*") return { t: "step", n };
    const r = parseRangeBounds(base, meta);
    if ("error" in r) return r;
    return { t: "rangeStep", a: r.a, b: r.b, n };
  }
  if (atom.includes("-")) {
    const r = parseRangeBounds(atom, meta);
    if ("error" in r) return r;
    return { t: "range", a: r.a, b: r.b };
  }
  const v = resolveValue(atom, meta);
  if (v === null) return { error: `Invalid value "${atom}" in ${meta.key}.` };
  if (v < meta.min || v > meta.max) {
    return {
      error: `"${atom}" is out of range for ${meta.key} (${meta.min}-${meta.max}).`,
    };
  }
  return { t: "single", v };
}

function parseRangeBounds(
  str: string,
  meta: FieldMeta,
): { a: number; b: number } | { error: string } {
  const parts = str.split("-");
  if (parts.length !== 2)
    return { error: `Invalid range "${str}" in ${meta.key}.` };
  const a = resolveValue(parts[0], meta);
  const b = resolveValue(parts[1], meta);
  if (a === null || b === null) {
    return { error: `Invalid range "${str}" in ${meta.key}.` };
  }
  if (a < meta.min || a > meta.max || b < meta.min || b > meta.max) {
    return {
      error: `Range "${str}" is out of bounds for ${meta.key} (${meta.min}-${meta.max}).`,
    };
  }
  if (a > b) return { error: `Range "${str}" is reversed in ${meta.key}.` };
  return { a, b };
}

function parseField(spec: string, meta: FieldMeta): AtomOrError {
  if (spec === "*") return { t: "all" };
  if (spec.includes(",")) {
    const items: Kind[] = [];
    for (const part of spec.split(",")) {
      const k = parseAtom(part, meta);
      if ("error" in k) return k;
      items.push(k);
    }
    return { t: "list", items };
  }
  return parseAtom(spec, meta);
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function valueLabel(v: number, meta: FieldMeta): string {
  if (meta.key === "month") return MONTH_NAMES[v - 1];
  if (meta.key === "dow") return DOW_NAMES[v === 7 ? 0 : v];
  return String(v);
}

/** Bare labeled values (no unit), used in the combined day/month phrases. */
function valueText(k: Kind, meta: FieldMeta): string {
  switch (k.t) {
    case "single":
      return valueLabel(k.v, meta);
    case "range":
      return `${valueLabel(k.a, meta)} through ${valueLabel(k.b, meta)}`;
    case "rangeStep":
      return `every ${k.n} from ${valueLabel(k.a, meta)} through ${valueLabel(k.b, meta)}`;
    case "step":
      return `every ${k.n}`;
    case "list":
      return k.items.map((i) => valueText(i, meta)).join(", ");
    default:
      return "";
  }
}

/** Human summary for the per-field breakdown. */
function fieldSummary(k: Kind, meta: FieldMeta): string {
  if (k.t === "all") return meta.allLabel;
  if (k.t === "step") return `Every ${k.n} ${meta.unitPlural}`;
  const prefix =
    meta.key === "minute"
      ? "Minute "
      : meta.key === "hour"
        ? "Hour "
        : meta.key === "dom"
          ? "Day "
          : "";
  if (k.t === "single") {
    return meta.key === "month" || meta.key === "dow"
      ? valueLabel(k.v, meta)
      : `${prefix}${k.v}`;
  }
  return valueText(k, meta);
}

function buildTime(min: Kind, hour: Kind): string {
  if (min.t === "single" && min.v === 0 && hour.t === "step") {
    return `every ${hour.n} hours`;
  }
  if (min.t === "single" && hour.t === "single") {
    return `at ${pad(hour.v)}:${pad(min.v)}`;
  }
  if (min.t === "all") {
    return hour.t === "all"
      ? "every minute"
      : `every minute past hour ${valueText(hour, META.hour)}`;
  }
  if (min.t === "step") {
    return hour.t === "all"
      ? `every ${min.n} minutes`
      : `every ${min.n} minutes past hour ${valueText(hour, META.hour)}`;
  }
  if (min.t === "single" && hour.t === "all") {
    return `at minute ${min.v} of every hour`;
  }
  return `at minute ${valueText(min, META.minute)} past hour ${valueText(hour, META.hour)}`;
}

function buildDay(dom: Kind, month: Kind, dow: Kind): string {
  const parts: string[] = [];
  if (dom.t !== "all")
    parts.push(`on day-of-month ${valueText(dom, META.dom)}`);
  if (dow.t !== "all") parts.push(`on ${valueText(dow, META.dow)}`);
  if (month.t !== "all") parts.push(`in ${valueText(month, META.month)}`);
  return parts.length > 0 ? parts.join(", ") : "every day";
}

function capitalize(s: string): string {
  return s.length > 0 ? s[0].toUpperCase() + s.slice(1) : s;
}

export function explainCron(expression: string): ExplainResult {
  const trimmed = expression.trim();
  if (!trimmed) return { ok: false, error: "Enter a cron expression." };

  const tokens = trimmed.split(/\s+/);
  if (tokens.length !== 5) {
    return {
      ok: false,
      error: `Expected 5 fields (minute hour day-of-month month day-of-week), got ${tokens.length}.`,
    };
  }

  const order: CronFieldKey[] = ["minute", "hour", "dom", "month", "dow"];
  const kinds = {} as Record<CronFieldKey, Kind>;
  const fields = {} as Record<CronFieldKey, string>;

  for (let i = 0; i < 5; i++) {
    const key = order[i];
    const meta = META[key];
    const parsed = parseField(tokens[i], meta);
    if ("error" in parsed) return { ok: false, error: parsed.error };
    kinds[key] = parsed;
    fields[key] = fieldSummary(parsed, meta);
  }

  const time = buildTime(kinds.minute, kinds.hour);
  const day = buildDay(kinds.dom, kinds.month, kinds.dow);

  let text: string;
  if (day === "every day") {
    text = time.startsWith("every")
      ? capitalize(time)
      : `${capitalize(time)}, every day`;
  } else {
    text = `${capitalize(time)}, ${day}`;
  }

  return { ok: true, text, fields };
}
